import { Fragment, useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Tooltip, TooltipTrigger } from '@design-system';
import { CareEnvironment } from '@shared/appModel';
import { WhenTooltipContent } from './cells';
import { currency, monthDisplay } from './formatters';
import { milestoneBadgeClass } from './milestoneColors';
import { ReasonText } from './ReasonText';
import { PreSegment, buildPreSegments, reasonForMonth } from './segments';
import { getCumulativeIneligibleCostSeries } from '@shared/calculations/funding/projection/projectionTotals';

function getRelevantWpDays(
  waitingPeriod: { homeDays: number; facilityDays: number },
  careEnvironment: CareEnvironment,
): number {
  return careEnvironment === CareEnvironment.home
    ? waitingPeriod.homeDays
    : waitingPeriod.facilityDays;
}

interface PrePayoutTableProps {
  months: MonthlyEntry[];
  initialState: MonthlyState;
}

/**
 * Summary view of the eligibility gap (months before any payout). Groups
 * consecutive same-reason months into one collapsible segment row.
 *
 * Single-column header (no group row), four columns:
 *   When · Projected Cost · Cumulative Out of Pocket · Reason
 */
export function PrePayoutTable({ months, initialState }: PrePayoutTableProps) {
  const segments = useMemo(
    () => buildPreSegments(months, initialState),
    [months, initialState],
  );
  const cumIneligibleSeries = useMemo(
    () => getCumulativeIneligibleCostSeries(months),
    [months],
  );
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div>
      <div className="max-h-[70vh] overflow-auto">
        <table className="min-w-full border-collapse text-xs">
          <thead className="sticky top-0 z-20 bg-white shadow-[0_1px_0_rgba(0,0,0,0.08)]">
            <tr>
              <th className="sticky left-0 z-10 bg-white px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-700 shadow-[1px_0_0_rgba(0,0,0,0.06)]">
                Policy month
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-700">
                Care cost<sup className="ml-0.5 font-normal text-gray-500">*</sup>
              </th>
              <th className="px-3 py-2 text-right text-[10px] font-semibold uppercase tracking-wide text-gray-700">
                Cumulative Out of Pocket
              </th>
              <th className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-gray-700">
                Reason
              </th>
            </tr>
          </thead>
          <tbody>
            {segments.map(segment => {
              const isExpanded = expanded.has(segment.id);
              const isMulti = segment.months.length > 1;
              return (
                <Fragment key={segment.id}>
                  <SegmentSummaryRow
                    segment={segment}
                    expanded={isExpanded}
                    onToggle={isMulti ? () => toggle(segment.id) : undefined}
                  />
                  {isExpanded &&
                    isMulti &&
                    segment.months.map(month => {
                      const globalIdx = months.indexOf(month);
                      return (
                        <PreDetailRow
                          key={month.context.monthIndex}
                          month={month}
                          cumulativeIneligible={cumIneligibleSeries[globalIdx]}
                          wpTotalDays={getRelevantWpDays(
                            segment.openStateAtStart.waitingPeriod,
                            month.context.careEnvironment,
                          )}
                        />
                      );
                    })}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SegmentSummaryRow({
  segment,
  expanded,
  onToggle,
}: {
  segment: PreSegment;
  expanded: boolean;
  onToggle?: () => void;
}) {
  const isMulti = segment.months.length > 1;
  const start = monthDisplay(segment.startMonthIndex);
  const end = monthDisplay(segment.endMonthIndex);
  const whenLabel = isMulti ? `Months ${start}–${end}` : `Month ${start}`;
  const firstMonth = segment.months[0];
  const lastMonth = segment.months[segment.months.length - 1];
  const costLabel =
    segment.minCost === segment.maxCost
      ? `${currency(segment.minCost)}/mo`
      : `${currency(segment.minCost)} – ${currency(segment.maxCost)}/mo`;

  const allMilestones = segment.months.flatMap(m => m.events.milestones);

  return (
    <tr
      className={`border-b border-gray-100 ${
        onToggle ? 'cursor-pointer hover:bg-gray-50' : ''
      }`}
      onClick={onToggle}
    >
      <td className="sticky left-0 z-[1] bg-white px-3 py-2.5 shadow-[1px_0_0_rgba(0,0,0,0.06)]">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-1.5">
            {onToggle && (
              <ChevronRight
                className={`h-3.5 w-3.5 text-gray-700 transition-transform ${
                  expanded ? 'rotate-90' : ''
                }`}
              />
            )}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-sm font-semibold text-gray-900 cursor-help">
                  {whenLabel}
                </span>
              </TooltipTrigger>
              <WhenTooltipContent startMonth={firstMonth} endMonth={lastMonth} />
            </Tooltip>
            {isMulti && (
              <span className="text-[11px] text-gray-500">({segment.months.length} mo)</span>
            )}
          </div>
          {allMilestones.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {allMilestones.map((m, mi) => (
                <span
                  key={mi}
                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap ${milestoneBadgeClass(m.kind)}`}
                >
                  {m.label}
                </span>
              ))}
            </div>
          )}
        </div>
      </td>
      <td className="px-3 py-2.5 text-right text-xs tabular-nums text-gray-600">{costLabel}</td>
      <td className="px-3 py-2.5 text-right text-base font-bold tabular-nums text-rose-700">
        {currency(segment.endCumulativeIneligible)}
      </td>
      <td className="px-3 py-2.5 text-left">
        <ReasonText
          reason={segment.reason}
          trailing={
            segment.reason.key === 'in-wp' && (
              <span className="ml-2 text-[11px] text-gray-500">
                {getRelevantWpDays(
                  segment.openStateAtStart.waitingPeriod,
                  firstMonth.context.careEnvironment,
                )}
                d total
              </span>
            )
          }
        />
      </td>
    </tr>
  );
}

function PreDetailRow({
  month,
  cumulativeIneligible,
  wpTotalDays,
}: {
  month: MonthlyEntry;
  cumulativeIneligible: number;
  wpTotalDays: number;
}) {
  const reason = reasonForMonth(month);
  const wpRemaining = getRelevantWpDays(
    month.state.waitingPeriod,
    month.context.careEnvironment,
  );
  const wpElapsed = Math.max(0, wpTotalDays - wpRemaining);
  return (
    <tr className="border-b border-gray-100 bg-gray-50/40">
      <td className="sticky left-0 z-[1] bg-gray-50/40 px-3 py-1.5 pl-8 shadow-[1px_0_0_rgba(0,0,0,0.06)]">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-xs text-gray-700 cursor-help">
              Month {monthDisplay(month.context.monthIndex)}
            </span>
          </TooltipTrigger>
          <WhenTooltipContent startMonth={month} />
        </Tooltip>
      </td>
      <td className="px-3 py-1.5 text-right text-xs tabular-nums text-gray-500">
        {currency(month.facts.effectiveCareCost)}
      </td>
      <td className="px-3 py-1.5 text-right text-xs tabular-nums text-rose-700">
        {currency(cumulativeIneligible)}
      </td>
      <td className="px-3 py-1.5 text-left">
        <ReasonText
          reason={reason}
          className="text-xs text-gray-600"
          trailing={
            reason.key === 'in-wp' && wpTotalDays > 0 ? (
              <span className="ml-1 text-gray-400">· {wpElapsed}d elapsed</span>
            ) : null
          }
        />
      </td>
    </tr>
  );
}
